import {
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";

import {
  getMachineCalcStorageKey,
  loadLocalCalcFields,
} from "@/features/machines/localCalculations";
import type { GhmSignalKind } from "@ghm/mqtt-contract";

import type { CalcField, Machine } from "@/features/machines/types";
import { formatTag, parseTag } from "@/features/machines/addressing";
import type { LatestPort, ProductionHistoryWindow } from "@/features/devices/types";
import { buildDefaultSignalConfig, mergeSignalConfig } from "@/features/signals/defaults";
import { indexSignalConfigs } from "@/features/signals/localSignals";
import type { SignalConfig } from "@/features/signals/types";

import {
  resolveWidgetValue,
  resolveWidgetValues,
  type SlotRuntimeBySlot,
} from "../selectors";
import {
  CELL_GAP,
  CELL_SIZE,
  cellsFromPixels,
  clampCells,
  clampToColumns,
  columnsForWidth,
  fits,
  widgetCell,
  widgetCells,
  type CellRect,
  type CellSize,
} from "../cellGrid";
import { groupWidgetsBySection } from "../sections";
import type { DashboardWidget, WidgetThreshold, WidgetType } from "../types";
import { isMultiSourceWidget, widgetSources } from "../types";
import { applyAccumulatorDelta, useAccumulatorDeltas } from "../useAccumulatorDeltas";
import { resolveProductionValueSource } from "../productionSources";
import { declaredProductionRateUnit } from "../heroProduction";
import { useAvailability } from "../useAvailability";
import { usePerformanceMilestone } from "@/lib/usePerformanceMilestone";
import { historyKeyFor, useProductionHistories } from "../useProductionHistories";
import { useProductionTrackers } from "../useProductionTrackers";
import { SignalCard } from "./SignalCard";
import { hasCurrentReading, type CurrentReadingProof } from "../currentReadingReadiness";

interface WidgetPanelProps {
  /** Mantém layout montado; false suspende consultas e trabalho dos cards ocultos. */
  active?: boolean;
  /** Instrumentacao opt-in; nenhuma alteracao no conteudo ou requisicoes. */
  performanceContext?: object;
  onCurrentReadingReadyChange?: (proof: CurrentReadingProof) => void;
  machine: Machine;
  latestPorts: LatestPort[];
  /** Hash do meta atual; invalida cache historico antes da primeira leitura nova. */
  configHashesByDevice?: ReadonlyMap<string, string | null>;
  /**
   * Mapa slotIndex → runtime (online + janela de frescor derivada da
   * cadência). Alimenta o modelo de qualidade (P0): distingue "aguardando
   * telemetria" (online) de "offline" e quando o valor vira "desatualizado".
   */
  slotRuntime?: SlotRuntimeBySlot;
  starterSignals?: StarterSignalDefinition[];
  /**
   * Widgets que o operador persistiu — quando vazio ou ausente, o painel
   * usa a prévia automática derivada dos `starterSignals`. A persistência
   * é responsabilidade do pai (via `useDashboardWidgets`).
   */
  savedWidgets?: DashboardWidget[];
  /**
   * Configurações de sinal (override de label/unidade/escala/limites/etc)
   * pelo operador. Aplicadas como camada sobre os starters e os saved
   * widgets cujo `source` aponta para um G-tag. Persistência via
   * `useSignalConfigs` no pai.
   */
  signalConfigs?: SignalConfig[];
  /** Rótulos publicados no meta, usados quando uma fonte ainda não tem SignalConfig. */
  sourceLabels?: Record<string, string>;
  /** Semantica por fonte publicada no meta; cura widgets antigos sem `signalKind`. */
  sourceKinds?: Record<string, GhmSignalKind>;
  /** Nome DECLARADO por fonte; devolve identidade a card de titulo automatico. */
  sourceNames?: Record<string, string>;
  /** Unidades DECLARADAS de todas as fontes, inclusive as sem starter (Modbus). */
  sourceUnits?: Readonly<Record<string, string | null | undefined>>;
  readOnly?: boolean;
  /** Persiste o tamanho em celulas apos o arrasto do canto. */
  onResizeWidget?: (widgetId: string, cells: CellSize) => void;
  /** Persiste a posicao de TODOS os cards — ver `aplicarMovimento`. */
  onMoveWidget?: (posicoes: Array<{ id: string; x: number; y: number }>) => void;
  /** Total esperado de widgets visíveis (alimenta o badge "previa automatica"). */
  onCountChange?: (count: number) => void;
  /**
   * Notifica o pai sobre sinais que o operador ainda nao tem widget salvo.
   * O pai pode oferecer uma acao "Adicionar widgets faltantes" no header
   * da secao. Util para evitar que widgets salvos antigos escondam sinais
   * novos publicados pelo gateway depois de uma mudanca de configuracao.
   */
  onMissingSourcesChange?: (sources: string[]) => void;
  /**
   * Notifica o pai sobre sinais ocultados pelo operador. Permite mostrar
   * algo como "3 sinais ocultos" no header da seção.
   */
  onHiddenCountChange?: (count: number) => void;
  /**
   * Pedido para editar o widget — o pai abre o `WidgetEditorModal`. O
   * widget passado já está com o estado resolvido (merge de
   * signalConfig + saved).
   */
  onEditWidget?: (widget: DashboardWidget) => void;
  /**
   * Reordena os widgets persistidos. Quando o operador arrasta a prévia
   * automática, o pai recebe esses starters e passa a salvá-los como layout.
   */
  /**
   * Gera um gráfico a partir de um widget numérico (menu `…` em
   * Visualização). O pai cria/persiste o widget de gráfico.
   */
  onGenerateChart?: (widget: DashboardWidget) => void;
  /**
   * Troca a janela histórica direto no card (seletor 24h/48h/7d/30d). O pai
   * persiste a escolha no widget (`applyWidgetWindow` + upsert).
   */
  onChangeWidgetWindow?: (widget: DashboardWidget, window: ProductionHistoryWindow) => void;
  /** Desfaz o "Gerar grafico" de um card que nasceu de uma substituicao. */
  onHideChart?: (widget: DashboardWidget) => void;
  /**
   * Relógio de observação do painel, atualizado também no retorno da aba.
   * Não é a data da amostra nem fica congelado no último sync bem-sucedido.
   */
  referenceNow?: Date;
}

export interface StarterSignalDefinition {
  deviceId: string;
  slotIndex: number;
  portIndex: number;
  mode?: string | number | null;
  signal: "s" | "acc" | "v";
  label?: string | null;
  unit?: string | null;
  /**
   * Tipo de widget vindo do catálogo (P1). Quando definido, vence o tipo
   * inferido por sinal — é o motor de coerência widget↔sinal decidindo.
   */
  widgetType?: WidgetType;
  /** Semântica declarada no `/meta` (`resolveSignalKind`). */
  kind?: GhmSignalKind;
  /** Faixa de engenharia declarada — o analógico a publica. */
  min?: number | null;
  max?: number | null;
  /** Casas decimais declaradas na configuração. */
  decimals?: number | null;
}

/**
 * Painel de widgets operacionais. Renderiza somente a grade — o cabeçalho
 * da seção e o empty state ficam por conta da página que monta o painel
 * (`MachineDashboardPage`), para combinar com o design novo.
 *
 * O painel NÃO possui o seu próprio modal de edição. Ao clicar na
 * engrenagem, ele emite `onEditWidget(widget)` para o pai, que monta o
 * `WidgetEditorModal` unificado e cuida da persistência.
 */
export const WidgetPanel = memo(function WidgetPanel({
  active = true,
  machine,
  latestPorts,
  configHashesByDevice,
  slotRuntime,
  starterSignals,
  savedWidgets = [],
  signalConfigs = [],
  sourceLabels: metaSourceLabels = {},
  sourceKinds: metaSourceKinds = {},
  sourceNames: metaSourceNames = {},
  sourceUnits: metaSourceUnits = {},
  readOnly = false,
  onResizeWidget,
  onMoveWidget,
  onCountChange,
  onMissingSourcesChange,
  onHiddenCountChange,
  onEditWidget,
  onGenerateChart,
  onChangeWidgetWindow,
  onHideChart,
  referenceNow,
  performanceContext,
  onCurrentReadingReadyChange,
}: WidgetPanelProps) {
  const [calcFields, setCalcFields] = useState<CalcField[]>([]);
  /**
   * Redimensionamento em curso. Guarda o alvo em CELULAS para o card mostrar
   * onde vai parar; nada e gravado ate soltar.
   */
  const [resizing, setResizing] = useState<{ id: string; cells: CellSize } | null>(null);
  /**
   * Movimento em curso. `livre` diz se o destino aceita o card — quando nao
   * aceita, o alvo aparece marcado e soltar CANCELA. Recusar em vez de
   * empurrar o vizinho mantem a promessa: o card fica onde foi solto, ou nao
   * vai; e o buraco que o operador deixou de proposito continua la.
   */
  const [moving, setMoving] = useState<{
    id: string;
    rect: CellRect;
    livre: boolean;
  } | null>(null);
  /**
   * A grade que CONTEM este card.
   *
   * Cada secao nomeada (e cada gateway, quando ha mais de um) renderiza a sua
   * propria `.wgrid`. Um unico `ref` para todas guardava apenas a ULTIMA
   * montada: medir contra ela nao encontrava o card arrastado em nenhuma outra
   * secao, e o movimento — arrasto e teclado — simplesmente nao acontecia,
   * sem erro nenhum. Perguntar ao proprio elemento em qual grade ele esta e a
   * unica resposta que nao depende da ordem de montagem.
   */
  const gradeDe = (el: HTMLElement | null): HTMLElement | null =>
    el?.closest<HTMLElement>(".wgrid") ?? null;

  /**
   * Retangulo de celulas de cada card da GRADE informada, MEDIDO no DOM.
   *
   * Medir em vez de calcular porque parte dos cards ainda flui
   * automaticamente, e onde o `auto-flow` os colocou so o navegador sabe.
   * Reimplementar o empacotamento do CSS aqui seria uma segunda verdade
   * pronta para divergir da primeira.
   */
  const medirOcupacao = (
    grid: HTMLElement | null,
  ): { colunas: number; rects: Map<string, CellRect> } => {
    const rects = new Map<string, CellRect>();
    if (!grid) return { colunas: 1, rects };
    const g = grid.getBoundingClientRect();
    const passo = CELL_SIZE + CELL_GAP;
    for (const el of grid.querySelectorAll<HTMLElement>("[data-widget-id]")) {
      const r = el.getBoundingClientRect();
      rects.set(el.dataset.widgetId as string, {
        x: Math.round((r.left - g.left) / passo),
        y: Math.round((r.top - g.top) / passo),
        w: Math.max(1, Math.round((r.width + CELL_GAP) / passo)),
        h: Math.max(1, Math.round((r.height + CELL_GAP) / passo)),
      });
    }
    return { colunas: columnsForWidth(g.width), rects };
  };

  /**
   * Grava a posicao de TODOS os cards, nao so a do movido.
   *
   * O primeiro movimento congela o painel: sem isso, dar coordenada a um card
   * faz o `auto-flow` reacomodar os demais em volta dele, e o operador mexe em
   * um enquanto tres saltam de lugar.
   */
  const aplicarMovimento = (
    grid: HTMLElement | null,
    widgetId: string,
    destino: CellRect,
  ) => {
    if (!onMoveWidget) return;
    const { rects } = medirOcupacao(grid);
    rects.set(widgetId, destino);
    onMoveWidget([...rects].map(([id, rect]) => ({ id, x: rect.x, y: rect.y })));
  };

  const iniciarMovimento = (widget: DashboardWidget, event: ReactPointerEvent<HTMLDivElement>) => {
    const alvoEl = event.currentTarget;
    alvoEl.setPointerCapture(event.pointerId);
    const grid = gradeDe(alvoEl);
    const { colunas, rects } = medirOcupacao(grid);
    const inicial = rects.get(widget.id);
    if (!inicial) return;
    const outros = [...rects].filter(([id]) => id !== widget.id).map(([, r]) => r);
    const startX = event.clientX;
    const startY = event.clientY;
    let destino = inicial;

    const mover = (move: PointerEvent) => {
      const delta = cellsFromPixels(move.clientX - startX, move.clientY - startY);
      destino = clampToColumns(
        { ...inicial, x: inicial.x + delta.w, y: inicial.y + delta.h },
        colunas,
      );
      setMoving({ id: widget.id, rect: destino, livre: fits(destino, outros, colunas) });
    };
    const soltar = () => {
      alvoEl.removeEventListener("pointermove", mover);
      alvoEl.removeEventListener("pointerup", soltar);
      alvoEl.removeEventListener("pointercancel", soltar);
      const mudou = destino.x !== inicial.x || destino.y !== inicial.y;
      const livre = fits(destino, outros, colunas);
      setMoving(null);
      // Destino ocupado: o card volta. Nada e gravado.
      if (mudou && livre) aplicarMovimento(grid, widget.id, destino);
    };
    alvoEl.addEventListener("pointermove", mover);
    alvoEl.addEventListener("pointerup", soltar);
    alvoEl.addEventListener("pointercancel", soltar);
  };

  /** Mesma regra do arrasto, uma celula por vez — o caminho de teclado. */
  const moverPorTeclado = (
    widget: DashboardWidget,
    dx: number,
    dy: number,
    grid: HTMLElement | null,
  ) => {
    const { colunas, rects } = medirOcupacao(grid);
    const atual = rects.get(widget.id);
    if (!atual) return;
    const outros = [...rects].filter(([id]) => id !== widget.id).map(([, r]) => r);
    const destino = clampToColumns({ ...atual, x: atual.x + dx, y: atual.y + dy }, colunas);
    if (!fits(destino, outros, colunas)) return;
    aplicarMovimento(grid, widget.id, destino);
  };
  const accumulatorDeltas = useAccumulatorDeltas(machine, latestPorts, active);

  useEffect(() => {
    const calcKey = getMachineCalcStorageKey(machine.workspaceId, machine.id);
    setCalcFields(loadLocalCalcFields(window.localStorage, calcKey, machine.id));
  }, [machine.id, machine.workspaceId]);

  const signalConfigIndex = useMemo(() => indexSignalConfigs(signalConfigs), [signalConfigs]);
  const starterWidgets = useMemo(
    () => buildStarterWidgets(machine, latestPorts, signalConfigIndex, starterSignals),
    [latestPorts, machine, signalConfigIndex, starterSignals],
  );
  const widgets = savedWidgets.length > 0 ? savedWidgets : starterWidgets;
  const sortedWidgets = useMemo(
    () => [...widgets].sort((left, right) => left.layout.order - right.layout.order),
    [widgets],
  );
  // Widgets que o operador marcou como hidden no SignalConfigModal ficam
  // fora da grade — mas continuam no storage para o operador poder
  // restaurar pelo botão "Voltar ao padrão" no modal.
  const visibleWidgets = useMemo(
    () =>
      sortedWidgets.filter((widget) => {
        const config = signalConfigIndex.get(widget.source);
        return !config?.hidden;
      }),
    [signalConfigIndex, sortedWidgets],
  );
  const hiddenCount = sortedWidgets.length - visibleWidgets.length;
  const currentReadingReady = active && performanceContext !== undefined && hasCurrentReading({
    widgets: visibleWidgets, machine, latestPorts, configHashes: configHashesByDevice,
    // O catalogo nao cria starters para MB; as chaves do mapa de fontes
    // do meta incluem tambem os sinais usados por widgets salvos de Modbus.
    declaredSources: new Set(Object.keys(metaSourceLabels)),
    signalConfigs: signalConfigIndex, sourceKinds: metaSourceKinds, slotRuntime,
    now: referenceNow ?? new Date(),
  });
  useLayoutEffect(() => {
    if (performanceContext) onCurrentReadingReadyChange?.({ context: performanceContext, ready: currentReadingReady });
  }, [currentReadingReady, onCurrentReadingReadyChange, performanceContext]);
  const productionHistories = useProductionHistories(machine, visibleWidgets, configHashesByDevice, active);
  const productionTrackers = useProductionTrackers(machine, visibleWidgets, active);
  const availabilityStates = useAvailability(machine, visibleWidgets, configHashesByDevice, active);
  usePerformanceMilestone("widgets_commit", active && visibleWidgets.length > 0);
  usePerformanceMilestone("histories_commit", active && productionHistories.size > 0 && [...productionHistories.values()].every((entry) => entry.status === "ready"));
  usePerformanceMilestone("availability_commit", active && availabilityStates.size > 0 && [...availabilityStates.values()].every((entry) => entry.status === "ready"));

  useEffect(() => {
    onCountChange?.(visibleWidgets.length);
  }, [onCountChange, visibleWidgets.length]);

  useEffect(() => {
    onHiddenCountChange?.(hiddenCount);
  }, [hiddenCount, onHiddenCountChange]);

  // Quando ha widgets salvos, compara com os starters (vindos do
  // metaJson.functions atualizado) e reporta sinais que o gateway publica
  // mas que o operador ainda nao adicionou ao dashboard.
  useEffect(() => {
    if (!onMissingSourcesChange) return;
    if (savedWidgets.length === 0) {
      onMissingSourcesChange([]);
      return;
    }
    const savedSources = new Set(savedWidgets.map((widget) => widget.source));
    const missing = starterWidgets
      .map((widget) => widget.source)
      .filter((source) => !savedSources.has(source));
    onMissingSourcesChange(missing);
  }, [onMissingSourcesChange, savedWidgets, starterWidgets]);

  if (visibleWidgets.length === 0) {
    return null;
  }

  // Agrupa por dispositivo (slot do gateway). Quando há mais de um grupo,
  // cada um ganha um cabeçalho — resolve a confusão de ver sinais de vários
  // gateways num grid plano. Com um único dispositivo, o cabeçalho é
  // omitido e o layout fica idêntico ao anterior.
  const groups = groupWidgetsBySection(visibleWidgets, machine);
  // Ordem do DOM = ordem de LEITURA da malha (linha, depois coluna). E o que
  // o telefone usa quando a posicao explicita e desligada e tudo vira uma
  // coluna so — sem isto, o celular mostraria a ordem de criacao.
  for (const grupo of groups) {
    grupo.widgets = [...grupo.widgets].sort((a, b) => {
      const pa = widgetCell(a);
      const pb = widgetCell(b);
      if (!pa || !pb) return 0;
      return pa.y - pb.y || pa.x - pb.x;
    });
  }
  // Cabecalho so aparece onde ha NOME. Sem secao nomeada o painel volta a ser
  // a grade plana de sempre — agrupar e escolha do operador, nao default.
  const showGroupHeaders = groups.some((group) => group.label !== null);

  // Reordenação acontece DENTRO de um grupo. A lista global persistida é
  // reconstruída concatenando os grupos na ordem dos slots — assim a ordem
  // salva fica naturalmente agrupada por dispositivo.
  const renderCard = (widget: DashboardWidget) => {
    const productionMode = widget.settings.production?.mode ?? "rate";
    const valueSource =
      widget.widgetType === "production-kpi"
        ? resolveProductionValueSource(widget.source, productionMode)
        : widget.source;
    const valueWidget =
      valueSource === widget.source ? widget : { ...widget, source: valueSource };
    const value = resolveWidgetValue(
      valueWidget,
      machine,
      latestPorts,
      calcFields,
      slotRuntime,
      referenceNow,
    );
    if (!value) return null;
    const operationalValue = applyAccumulatorDelta(
      widget.widgetType,
      widget.settings.availability?.mode,
      value,
      accumulatorDeltas,
    );
    // Widget comparativo precisa das N fontes. Resolvido só quando o tipo
    // pede: `resolveWidgetValues` percorre todas as tags, e pagar isso em todo
    // card de fonte única seria desperdício num painel com dezenas deles.
    // Card de saude Modbus: precisa das portas MB do MESMO slot da ancora.
    // Resolvido so quando o tipo pede — varrer `latestPorts` em todo card de
    // um painel com dezenas deles seria desperdicio.
    const modbusPorts =
      widget.widgetType === "modbus-health"
        ? (() => {
            const address = parseTag(widget.source);
            const deviceId = machine.slots.find(
              (slot) => slot.slotIndex === address?.slotIndex,
            )?.deviceId;
            return deviceId
              ? latestPorts.filter(
                  (port) =>
                    port.deviceId === deviceId &&
                    String(port.mode ?? "").trim().toUpperCase() === "MB",
                )
              : [];
          })()
        : undefined;
    // Disponibilidade historica mede MOVIMENTO. Numa porta que nao publica
    // `v`, o backend so sabe responder zero — e zero e indistinguivel de uma
    // parada real. O card precisa saber disso para dizer o que houve.
    const movimentoDeclarado =
      widget.widgetType === "utilization"
        ? portaDeclaraMovimento(widget.source, metaSourceLabels)
        : undefined;
    const multiSource = isMultiSourceWidget(widget);
    const sourceLabels = multiSource
      ? Object.fromEntries(
          widgetSources(widget).flatMap((source) => {
            // Dentro de um comparativo o rotulo do `/meta` vem PRIMEIRO. O
            // `SignalConfig.label` e o titulo que o operador deu a um card
            // individual daquela fonte ("Tensao da entrada principal — conferir
            // semanalmente"); ao lado de duas legendas curtas ele parece medir
            // outra coisa. Aqui o titulo do widget ja descreve o CONJUNTO, e a
            // legenda so precisa nomear a grandeza. O `SignalConfig` continua
            // valendo como reserva para fontes fora do `/meta` atual.
            const label =
              metaSourceLabels[source]?.trim() || signalConfigIndex.get(source)?.label?.trim();
            return label ? [[source, label] as const] : [];
          }),
        )
      : undefined;
    const allValues = multiSource
      ? resolveWidgetValues(widget, machine, latestPorts, calcFields, slotRuntime, referenceNow)
      : undefined;
    // Alinhado PELOS VALORES, nao por `widgetSources`. `resolveWidgetValues`
    // omite fonte que nao resolve, entao indexar os historicos pela lista
    // completa de fontes desalinharia os arrays e cada serie seria desenhada
    // com o historico da fonte seguinte — curvas trocadas, sem erro visivel.
    const allHistories = allValues
      ? allValues.map((resolved) =>
          productionHistories.get(historyKeyFor(widget.id, resolved.source)),
        )
      : undefined;
    // O tamanho vem das CELULAS que o operador arrastou. Enquanto arrasta,
    // `resizing`/`moving` mostram o alvo sem gravar nada — so o soltar
    // persiste.
    const cells = resizing?.id === widget.id ? resizing.cells : widgetCells(widget);
    const posSalva = widgetCell(widget);
    const pos = moving?.id === widget.id ? moving.rect : posSalva;
    const podeMover = !readOnly && !!onMoveWidget;
    return (
      <div
        key={widget.id}
        data-widget-id={widget.id}
        tabIndex={podeMover ? 0 : undefined}
        role={podeMover ? "application" : undefined}
        aria-label={podeMover ? `${widget.title}. Setas movem o card.` : undefined}
        style={
          pos
            ? {
                gridColumn: `${pos.x + 1} / span ${cells.w}`,
                gridRow: `${pos.y + 1} / span ${cells.h}`,
              }
            : { gridColumn: `span ${cells.w}`, gridRow: `span ${cells.h}` }
        }
        className={[
          "w-drag-item",
          resizing?.id === widget.id ? "w-drag-item--resizing" : "",
          moving?.id === widget.id
            ? moving.livre
              ? "w-drag-item--moving"
              : "w-drag-item--moving w-drag-item--blocked"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
          if (!podeMover) return;
          const passo: Record<string, [number, number]> = {
            ArrowLeft: [-1, 0],
            ArrowRight: [1, 0],
            ArrowUp: [0, -1],
            ArrowDown: [0, 1],
          };
          const delta = passo[event.key];
          if (!delta) return;
          event.preventDefault();
          moverPorTeclado(widget, delta[0], delta[1], gradeDe(event.currentTarget));
        }}
        onPointerDown={(event: ReactPointerEvent<HTMLDivElement>) => {
          if (!podeMover) return;
          // Botoes e a alca tem o proprio gesto; arrastar o card e o resto.
          if ((event.target as HTMLElement).closest("button")) return;
          iniciarMovimento(widget, event);
        }}
      >
        <SignalCard
          widget={widget}
          value={operationalValue}
          values={allValues}
          history={productionHistories.get(historyKeyFor(widget.id, widget.source))}
          histories={allHistories}
          sourceLabels={sourceLabels}
          sourceKind={metaSourceKinds[widget.source]}
          sourceName={metaSourceNames[widget.source]}
          sourceUnit={metaSourceUnits[widget.source]}
          sourceRateUnit={declaredProductionRateUnit(widget.source, metaSourceKinds, metaSourceUnits)}
          modbusPorts={modbusPorts}
          availability={availabilityStates.get(widget.id)}
          movimentoDeclarado={movimentoDeclarado}
          productionTracker={productionTrackers.states.get(widget.id)}
          onResetProduction={() => productionTrackers.reset(widget.id)}
          signalConfig={signalConfigIndex.get(widget.source)}
          // Todo widget é editável em modo edição — antes só os de
          // G-source eram editáveis, deixando V0/V1 sem como ajustar.
          editable={!readOnly}
          onGenerateChart={onGenerateChart}
          onHideChart={onHideChart}
          onChangeWindow={onChangeWidgetWindow}
          onConfigure={(selectedWidget) => {
            // Quando o operador clica na engrenagem, repassa o widget
            // (com o config layer já mesclado pelo SignalCard) para o
            // pai abrir o editor. O pai cuida da persistência.
            onEditWidget?.(selectedWidget);
          }}
        />
        {!readOnly && onResizeWidget ? (
          <button
            type="button"
            className="w-resize"
            aria-label={`Redimensionar ${widget.title}`}
            title="Arraste para redimensionar"
            onPointerDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              const handle = event.currentTarget;
              handle.setPointerCapture(event.pointerId);
              const startX = event.clientX;
              const startY = event.clientY;
              const inicial = widgetCells(widget);
              let alvo = inicial;

              const mover = (move: PointerEvent) => {
                const delta = cellsFromPixels(
                  move.clientX - startX,
                  move.clientY - startY,
                );
                alvo = clampCells(
                  { w: inicial.w + delta.w, h: inicial.h + delta.h },
                  widget.widgetType,
                );
                setResizing({ id: widget.id, cells: alvo });
              };
              const soltar = () => {
                handle.removeEventListener("pointermove", mover);
                handle.removeEventListener("pointerup", soltar);
                handle.removeEventListener("pointercancel", soltar);
                setResizing(null);
                // So persiste se mudou: um clique sem arrastar nao deve
                // gravar layout nem sujar o historico de edicao.
                if (alvo.w !== inicial.w || alvo.h !== inicial.h) {
                  onResizeWidget(widget.id, alvo);
                }
              };
              handle.addEventListener("pointermove", mover);
              handle.addEventListener("pointerup", soltar);
              handle.addEventListener("pointercancel", soltar);
            }}
          />
        ) : null}
      </div>
    );
  };

  // Caminho da grade plana: um grupo so, sem cabecalho — identico ao layout
  // anterior. A condicao precisa exigir UM GRUPO, e nao apenas "nenhum tem
  // nome": com dois gateways e nenhuma secao nomeada existem DOIS grupos sem
  // rotulo, e desestruturar `[onlyGroup]` fazia os widgets do segundo gateway
  // sumirem da tela inteira.
  const [onlyGroup] = groups;
  if (groups.length === 1 && onlyGroup && !showGroupHeaders) {
    return (
      <div className="wgrid">
        {onlyGroup.widgets.map((widget) => renderCard(widget))}
      </div>
    );
  }

  return (
    <>
      {groups.map((group) => (
        <section key={group.key} className="wgroup">
          {group.label ? (
          <div className="wgroup-h">
            <span className="wgroup-name">{group.label}</span>
            {group.deviceLabel ? (
              <span className="wgroup-tag mono">
                {group.deviceLabel}
                {group.slotIndex !== null ? ` · G${group.slotIndex}` : ""}
              </span>
            ) : null}
            <span className="wgroup-rule" aria-hidden="true" />
            <span className="wgroup-count">{group.widgets.length}</span>
          </div>
          ) : null}
          <div className="wgrid">
            {group.widgets.map((widget) => renderCard(widget))}
          </div>
        </section>
      ))}
    </>
  );
}, (previous, next) => {
  // O painel continua montado, conservando janelas/estado. Só ignoramos novas
  // props enquanto AMBOS os quadros estão ocultos. A retomada sempre renderiza
  // as props atuais; outra máquina/workflow nunca atravessa esta barreira.
  return previous.active === false && next.active === false &&
    previous.machine.workspaceId === next.machine.workspaceId &&
    previous.machine.id === next.machine.id;
});


export function buildStarterWidgets(
  machine: Machine,
  latestPorts: LatestPort[],
  signalConfigIndex: Map<string, SignalConfig>,
  starterSignals?: StarterSignalDefinition[],
): DashboardWidget[] {
  if (starterSignals) {
    return starterSignals.map((definition, order) => {
      const port =
        latestPorts.find(
          (candidate) =>
            candidate.deviceId === definition.deviceId &&
            candidate.portIndex === definition.portIndex,
        ) ?? buildSyntheticPort(definition);

      return buildStarterWidget(
        machine.id,
        definition.slotIndex,
        port,
        definition.signal,
        order,
        signalConfigIndex,
        definition.label,
        definition.unit,
        definition.widgetType,
        {
          kind: definition.kind,
          min: definition.min,
          max: definition.max,
          decimals: definition.decimals,
        },
      );
    });
  }

  return latestPorts.flatMap((port, portOrder): DashboardWidget[] => {
    const slot = machine.slots.find((candidate) => candidate.deviceId === port.deviceId);
    if (!slot || portOrder > 5) return [];
    const signal = chooseFallbackSignal(port);
    return [buildStarterWidget(machine.id, slot.slotIndex, port, signal, portOrder, signalConfigIndex)];
  });
}

function buildStarterWidget(
  machineId: string,
  slotIndex: number,
  port: LatestPort,
  signal: "s" | "acc" | "v",
  order: number,
  signalConfigIndex: Map<string, SignalConfig>,
  label?: string | null,
  unit?: string | null,
  preferredWidgetType?: WidgetType,
  declared?: {
    kind?: GhmSignalKind;
    min?: number | null;
    max?: number | null;
    decimals?: number | null;
  },
): DashboardWidget {
  const source = formatTag({
    kind: "real",
    slotIndex,
    portIndex: port.portIndex,
    signal,
  });
  const defaults = buildDefaultSignalConfig({
    machineId,
    slotIndex,
    port,
    signal,
    kind: declared?.kind,
    declaredRange: { min: declared?.min ?? undefined, max: declared?.max ?? undefined },
    declaredDecimals: declared?.decimals ?? undefined,
  });
  const labelledDefaults = {
    ...defaults,
    label: label?.trim() || defaults.label,
    unit: unit?.trim() || defaults.unit,
  };
  const config = mergeSignalConfig(labelledDefaults, signalConfigIndex.get(source));
  const max = config.scale?.max ?? 100;

  // A previa automatica nasce compacta; tamanho grande fica como escolha
  // explicita do operador no editor.
  const size = config.size ?? "md";

  /**
   * ALARME E ESCOLHA, NUNCA DEFAULT. Decisao do dono: o card nasce neutro.
   *
   * Aqui o card nascia com `warn` em 70 % e `danger` em 90 % do maximo, "para
   * ter um visual minimamente util sem configurar". O preco era alto e
   * silencioso: a escala nem sempre e declarada — sem faixa no `/meta` ela cai
   * em 0-100 —, entao os cortes saiam de um numero que ninguem escolheu. Uma
   * tensao de 220 V numa escala inventada de 250 nasce em ATENCAO; qualquer
   * grandeza que ande perto do fundo de escala nasce em PERIGO. Alarme que
   * dispara sozinho no primeiro dia ensina a ignorar alarme.
   *
   * Pior: o editor ja fazia o oposto — widget criado por la nasce sem alarme,
   * de proposito e por decisao registrada. Dois caminhos de criacao davam duas
   * respostas para a mesma pergunta, e o operador via "Atencao 7 / Perigo 9"
   * preenchidos no editor, indistinguiveis de uma escolha dele.
   *
   * Agora so vale o que o operador definiu no `SignalConfig`.
   */
  const thresholds: WidgetThreshold[] | undefined =
    config.thresholds && config.thresholds.length > 0 ? config.thresholds : undefined;

  return {
    id: `starter-${source}`,
    machineId,
    title: config.label,
    source,
    // O catálogo (P1) decide o tipo coerente com a semântica; sem ele, cai
    // na inferência por sinal do signalConfig.
    widgetType: preferredWidgetType ?? config.preferredWidget,
    settings: {
      signalKind: config.kind,
      unit: config.unit,
      min: config.scale?.min,
      max,
      decimals: config.scale?.decimals,
      activeWhen: config.activeWhen,
      stateLabels: config.stateLabels,
      thresholds,
    },
    layout: {
      size,
      order,
    },
  };
}

function buildSyntheticPort(definition: StarterSignalDefinition): LatestPort {
  return {
    deviceId: definition.deviceId,
    portIndex: definition.portIndex,
    mode: definition.mode,
    measuredAt: null,
    s: definition.signal === "s" ? null : undefined,
    acc: definition.signal === "acc" ? null : undefined,
    v: definition.signal === "v" ? null : undefined,
  };
}

function chooseFallbackSignal(port: LatestPort): "s" | "acc" | "v" {
  const mode = String(port.mode ?? "").toUpperCase();
  if ((mode === "DI" || mode === "LATCH" || mode === "RST") && port.s !== null && port.s !== undefined) {
    return "s";
  }
  if ((mode === "PM" || mode === "HSC") && port.v !== null && port.v !== undefined) {
    return "v";
  }
  if (port.acc !== null && port.acc !== undefined) return "acc";
  return "s";
}

/**
 * A porta ancora declara grandeza de MOVIMENTO (`_v`) no `/meta` atual?
 *
 * Disponibilidade e MOVIMENTO, nao energia: uma maquina ligada e parada nao
 * esta disponivel. Por isso a linha do tempo mede `v`. A consequencia e que uma
 * porta que nao publica `v` — uma entrada digital publica `{s, acc}` — nao tem
 * como ser medida, e o backend responde o unico numero que sabe formar: zero.
 *
 * Zero e indistinguivel de uma parada real. Este predicado existe para o card
 * poder dizer "nao da para medir" em vez de "parada a janela inteira".
 *
 * `undefined` = ainda nao da para saber, porque o `/meta` nao chegou. Aqui o
 * silencio e obrigatorio: um veredito tirado de mapa vazio acusaria toda porta
 * de nao ter movimento durante o primeiro quadro.
 */
function portaDeclaraMovimento(
  source: string,
  declaradas: Record<string, string>,
): boolean | undefined {
  if (Object.keys(declaradas).length === 0) return undefined;
  const tag = parseTag(source);
  if (!tag) return undefined;
  const movimento = formatTag({
    kind: "real",
    slotIndex: tag.slotIndex,
    portIndex: tag.portIndex,
    signal: "v",
  });
  return Object.prototype.hasOwnProperty.call(declaradas, movimento);
}
